import { motion } from 'framer-motion'
import { Link2, TrendingUp, Sparkles } from 'lucide-react'
import { AnimatedLineChart, DashboardPreview } from './AnimatedCharts'

const HowItWorks = () => {
  
  const steps = [
    {
      number: '01',
      icon: Link2,
      title: 'Konten verbinden',
      description:
        'Kein neues Bankkonto erstellen! Verbinde einfach deine bestehenden deutschen Bankkonten sicher über die PSD2-Schnittstelle. Nur sichere Tokens, keine Passwörter.',
      visual: (
        <div className="relative w-full">
          <div className="flex flex-wrap gap-4 justify-center items-center">
            {/* SK - Sparkasse Red */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-red-600 to-red-800 border-2 border-red-500 shadow-lg flex items-center justify-center hover:scale-110 hover:border-red-400 transition-all"
            >
              <span className="text-xl font-bold text-white">SK</span>
            </motion.div>
            
            {/* DB - Deutsche Bank Blue */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 border-2 border-blue-500 shadow-lg flex items-center justify-center hover:scale-110 hover:border-blue-400 transition-all"
            >
              <span className="text-xl font-bold text-white">DB</span>
            </motion.div>
            
            {/* CB - Commerzbank Yellow */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-500 to-yellow-600 border-2 border-yellow-400 shadow-lg flex items-center justify-center hover:scale-110 hover:border-yellow-300 transition-all"
            >
              <span className="text-xl font-bold text-gray-900">CB</span>
            </motion.div>
            
            {/* ING - Orange */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.9 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-700 border-2 border-orange-400 shadow-lg flex items-center justify-center hover:scale-110 hover:border-orange-300 transition-all"
            >
              <span className="text-xl font-bold text-white">ING</span>
            </motion.div>
            
            {/* N26 - Cyan/Turquoise */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1.2 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-600 border-2 border-cyan-400 shadow-lg flex items-center justify-center hover:scale-110 hover:border-cyan-300 transition-all"
            >
              <span className="text-xl font-bold text-white">N26</span>
            </motion.div>
            
            {/* DKB - Deep Blue */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: 1.5 }}
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-700 to-blue-900 border-2 border-blue-600 shadow-lg flex items-center justify-center hover:scale-110 hover:border-blue-500 transition-all"
            >
              <span className="text-xl font-bold text-white">DKB</span>
            </motion.div>
          </div>
          
          {/* Connection pulse center */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div 
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 blur-xl"
            />
          </div>
        </div>
      ),
    },
    {
      number: '02',
      icon: TrendingUp,
      title: 'Charts in Echtzeit sehen',
      description:
        'Beobachte deine Finanzen mit wunderschönen, interaktiven Diagrammen. Jede Transaktion wird sofort visualisiert.',
      visual: <AnimatedLineChart />,
    },
    {
      number: '03',
      icon: Sparkles,
      title: 'Geld sparen mit KI',
      description:
        'Erhalte personalisierte Tipps und folge intelligenten Sparplänen. Unsere KI hilft dir, mehr zu sparen.',
      visual: <DashboardPreview />,
    },
  ]

  return (
    <section id="how-it-works" className="py-12 lg:py-16 bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 via-black to-black pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-gradient-to-b from-blue-600/10 to-transparent blur-3xl pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            className="inline-block mb-4"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 px-6 py-2 rounded-full">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-gradient">3 einfache Schritte</span>
            </div>
          </motion.div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white">So einfach geht's</h2>
          <p className="text-xl text-gray-400">In nur 3 Schritten zu besseren Finanzen</p>
        </motion.div>

        <div className="space-y-32">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                {/* Step number badge */}
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className="inline-flex items-center gap-3 mb-6"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-6xl md:text-7xl font-black text-gradient">{step.number}</div>
                </motion.div>

                <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white">{step.title}</h3>
                <p className="text-lg md:text-xl text-gray-400 leading-relaxed">{step.description}</p>
              </div>
              
              <div
                className={`bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-gray-700/50 relative overflow-hidden group ${
                  index % 2 === 1 ? 'lg:order-1' : ''
                }`}
              >
                {/* Glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 via-purple-600/0 to-blue-600/0 group-hover:from-blue-600/10 group-hover:via-purple-600/10 group-hover:to-blue-600/10 transition-all duration-500" />
                
                {/* Content */}
                <div className="relative z-10 h-64 flex items-center justify-center">
                  {step.visual}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
